import { useCallback, forwardRef, useImperativeHandle, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { PresentUrl, addressFormApi, denaveAadharFetchApi, denaveStateGetApi, addressFormStateApi } from "../../../text/apidata.js";
import ImageCropper from "../ReactcropOverall/Imagecrop.js";
import { store } from "../../../Redux/Store.js";
import { SetTheStateGetListActions } from "../../../Redux/Slice.js";
import {
  removeAddressImage,
  selectEmployeeInformation,
  setAddressImage,
  setSameAsPermanent,
  updateAddressField,
} from "../../../Redux/employeeInformationSlice.js";
import { getLoaderAction2 } from "../../../Redux/Loader2.js";
import styles from "./AddressDetails.module.scss";

const requiredFields = [
  // Permanent Address
  { name: "street_name1", label: "Permanent Street name" },
  { name: "city_name1", label: "Permanent City" },
  { name: "state_name1", label: "Permanent State" },
  { name: "pin_code1", label: "Permanent Pincode" },

  // Present Address
  { name: "street_name", label: "Present Street name" },
  { name: "city_name", label: "Present City" },
  { name: "state_name", label: "Present State" },
  { name: "pin_code", label: "Present Pincode" },
];

const AddressDetails = forwardRef(function AddressDetails(_, ref) {
  const dispatch = useDispatch();

  const {
    addressDetails: form,
    sameAsPermanent,
    addressImages,
  } = useSelector(selectEmployeeInformation);

  const selfonboardData = useSelector(
    (state) => state.selfonboardingretriveslice?.Selfonboarding_Data
  );
  const employeeDetails = useSelector(
    (state) => state.Signature_Employee_RetrieveCall_actions?.Signature_Employee_Datas
  );
  const reduxStateList = useSelector(
    (state) => state.OverallStateCallData?.OverallStateGetList
  );

  const aadhaarBackImage = addressImages.aadhaarBack;

  const [errorFields, setErrorFields] = useState(new Set());
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperTargetName, setCropperTargetName] = useState("");
  const [selectedImageEvent, setSelectedImageEvent] = useState(null);
  const [stateList, setStateList] = useState([]);

  // const fetchedAddressFormIdRef = useRef(null);

  const extractAddressFormId = (d) => {
    if (!d) return null;
    const raw =
      d?.employeetimeline_id?.address_details ||
      d?.employeetimeline_id?.address_details_form ||
      d?.address_details_form?.id ||
      d?.address_details_form ||
      d?.address_details?.id ||
      d?.address_details ||
      d?.address_details_id ||
      d?.addressform_id;

    if (typeof raw === "object" && raw !== null) {
      return raw.id || raw._id || null;
    }
    return raw ? String(raw) : null;
  };

  const hasDispatchedStateFetchRef = useRef(false);
  const fetchedStateListRef = useRef(false);

  // // Dispatch Redux State Get List Action once on mount
  // useEffect(() => {
  //   if (!hasDispatchedStateFetchRef.current) {
  //     hasDispatchedStateFetchRef.current = true;
  //     dispatch(SetTheStateGetListActions({ page: 1 }));
  //   }
  // }, [dispatch]);

  // Direct API fetch fallback for State List once on mount
  useEffect(() => {
    if (fetchedStateListRef.current) return;
    fetchedStateListRef.current = true;

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    const handleSuccessList = (resData) => {
      const list =
        resData?.data?.results ||
        resData?.results ||
        resData?.data ||
        (Array.isArray(resData) ? resData : []);
      if (Array.isArray(list) && list.length > 0) {
        setStateList(list);
      }
    };

    const targetEndpoint = addressFormStateApi;

    axios
      .get(targetEndpoint, { headers, params: { page: 1 } })
      .then((res) => handleSuccessList(res.data))
      .catch(() => {
        axios
          .get(targetEndpoint, { params: { page: 1 } })
          .then((res) => handleSuccessList(res.data))
          .catch((err) => console.error("Error fetching state list:", err));
      });
  }, [selfonboardData, employeeDetails]);

  const activeStateList =
    Array.isArray(reduxStateList) && reduxStateList.length > 0
      ? reduxStateList
      : stateList;

  const matchStateId = (stName, list) => {
    if (!stName || stName === "Select") return "";
    const upper = String(stName).trim().toUpperCase();
    const upperNoSpace = upper.replace(/\s+/g, "");

    const listToSearch = Array.isArray(list) && list.length > 0 ? list : activeStateList;

    if (Array.isArray(listToSearch) && listToSearch.length > 0) {
      const match = listToSearch.find(
        (s) =>
          String(s.id).toUpperCase() === upper ||
          String(s.state_name || s.name || s.state || "").trim().toUpperCase() === upper ||
          String(s.state_name || s.name || s.state || "").trim().toUpperCase().replace(/\s+/g, "") === upperNoSpace
      );
      if (match && match.id) return match.id;
    }
    return upper;
  };

  const hasResolvedStatesRef = useRef(false);

  // Auto-resolve state text into state UUID once state list is available (runs once on load)
  useEffect(() => {
    if (activeStateList.length > 0 && !hasResolvedStatesRef.current) {
      let resolved = false;

      if (form.state_name1 && form.state_name1 !== "Select") {
        const resId1 = matchStateId(form.state_name1, activeStateList);
        if (resId1 && resId1 !== form.state_id1) {
          dispatch(updateAddressField({ name: "state_id1", value: resId1 }));
          dispatch(updateAddressField({ name: "state_name1", value: resId1 }));
          resolved = true;
        }
      }

      if (form.state_name && form.state_name !== "Select") {
        const resId = matchStateId(form.state_name, activeStateList);
        if (resId && resId !== form.state_id) {
          dispatch(updateAddressField({ name: "state_id", value: resId }));
          dispatch(updateAddressField({ name: "state_name", value: resId }));
          resolved = true;
        }
      }

      if (resolved || form.state_name1 || form.state_name) {
        hasResolvedStatesRef.current = true;
      }
    }
  }, [activeStateList.length]);

  const fetchedAddressFormIdRef = useRef(null);

  // GET Address Details API call on mount
  useEffect(() => {
    const details = selfonboardData || employeeDetails;
    if (!details) {
      dispatch(getLoaderAction2(false));
      return;
    }

    const addressFormId = extractAddressFormId(details);
    if (!addressFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }

    if (fetchedAddressFormIdRef.current === addressFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }
    fetchedAddressFormIdRef.current = addressFormId;

    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    axios
      .get(`${addressFormApi}${addressFormId}/`, { headers })
      .then((res) => {
        console.log("Address form GET API response:", res.data);
        const data = res.data?.data || res.data;
        if (data) {
          const pres =
            (typeof data.present_address === "object" && data.present_address !== null)
              ? data.present_address
              : data;

          const perm =
            (typeof data.permanent_address_id === "object" && data.permanent_address_id !== null)
              ? data.permanent_address_id
              : (typeof data.permanent_address === "object" && data.permanent_address !== null)
                ? data.permanent_address
                : pres;

          const extractStateId = (stateObj) => {
            if (!stateObj) return "";
            if (typeof stateObj === "object" && stateObj !== null) {
              return stateObj.id || stateObj.state_name || stateObj.name || "";
            }
            return String(stateObj);
          };

          const presStateVal = extractStateId(pres.state_name || data.state_name || data.state_id);
          const permStateVal = extractStateId(perm.state_name || data.state_name1 || data.state_name_1 || presStateVal);

          const addressFieldMap = {
            street_name: pres.street_name || data?.street_name || "",
            town_name: pres.town_name || data?.town_name || "",
            city_name: pres.city_name || data?.city_name || "",
            state_name: presStateVal || "Select",
            pin_code: pres.pin_code || data?.pin_code || "",
            state_id: presStateVal || "Select",

            street_name1: perm.street_name || data?.street_name1 || data?.street_name_1 || pres.street_name || data?.street_name || "",
            town_name1: perm.town_name || data?.town_name1 || data?.town_name_1 || pres.town_name || data?.town_name || "",
            city_name1: perm.city_name || data?.city_name1 || data?.city_name_1 || pres.city_name || data?.city_name || "",
            state_name1: permStateVal || presStateVal || "Select",
            pin_code1: perm.pin_code || data?.pin_code1 || data?.pin_code_1 || pres.pin_code || data?.pin_code || "",
            state_id1: permStateVal || presStateVal || "Select",
          };

          Object.entries(addressFieldMap).forEach(([name, value]) => {
            if (value && value !== "Select") {
              dispatch(updateAddressField({ name, value }));
            }
          });

          const isPerm =
            data?.permanent_address === true ||
            data?.is_permanent === true ||
            data?.same_as_permanent === true ||
            data?.permanent_address === "true" ||
            data?.is_permanent === "true";

          dispatch(setSameAsPermanent(Boolean(isPerm)));

          const aadharBackObj =
            data?.aadhar_back ||
            data?.aadharback_id ||
            data?.aadhar_back_id ||
            data?.aadhaar_back ||
            data?.aadhaarback_id ||
            data?.aadhar_id;

          const aadharBackUrl =
            (typeof aadharBackObj === "object" && aadharBackObj !== null
              ? aadharBackObj?.media_url || aadharBackObj?.url
              : typeof aadharBackObj === "string"
                ? aadharBackObj
                : "") || "";

          if (aadharBackUrl) {
            dispatch(
              setAddressImage({
                name: "aadhaarBack",
                image: {
                  name: "Aadhaar Back",
                  url: aadharBackUrl,
                  s3Url: aadharBackUrl,
                },
              })
            );
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching address form GET API:", err);
      })
      .finally(() => {
        dispatch(getLoaderAction2(false));
      });
  }, [dispatch, selfonboardData, employeeDetails,]);

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateAddressField({ name, value }));
    clearError(name);
  };

  const updateTextField = (name, value) => {
    updateField(name, value.toUpperCase());
  };

  const updatePincode = (name, value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    updateField(name, numericValue);
  };

  const handleFileInputChange = (name, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("File size exceeds 10MB");
      return;
    }

    setSelectedImageEvent(event);
    setCropperTargetName(name);
    setCropperOpen(true);
  };

  const getCroppedImg = (imageRef, crop, fileName) => {
    const canvas = document.createElement("canvas");
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], fileName, {
          type: "image/jpeg",
        });

        setCropperOpen(false);
        handleImageChange(cropperTargetName, croppedFile);
        resolve(croppedFile);
      }, "image/jpeg");
    });
  };

  const handleImageChange = async (name, file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (addressImages[name]?.url) {
      URL.revokeObjectURL(addressImages[name].url);
    }

    const localUrl = URL.createObjectURL(file);

    dispatch(
      setAddressImage({
        name,
        image: {
          name: file.name,
          url: localUrl,
          s3Url: "",
          file: file,
        },
      })
    );

    clearError(name);

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));
    try {
      // 1. Presigned S3 Upload matching BasicInformation format
      const folderName = "aadharback";
      const randomNumber = Math.floor(Math.random() * 10000000000);
      const fileExt = file.name.split(".").pop() || "jpg";
      const formattedFilename = `onboarding/${folderName}/image_cropper_${randomNumber}.${fileExt}`;

      const presignedReqData = {
        multiple_files: [
          {
            filename: formattedFilename,
            file_type: file.type || "image/jpeg",
          },
        ],
      };

      const presignedRes = await axios.post(PresentUrl, presignedReqData, { headers });
      const uploadedUrls = presignedRes.data?.data || presignedRes.data;

      let s3CleanUrl = "";
      if (Array.isArray(uploadedUrls) && uploadedUrls.length > 0) {
        const presignedPutUrl = uploadedUrls[0];
        s3CleanUrl = String(presignedPutUrl).split("?")[0];

        await fetch(presignedPutUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "image/jpeg",
            "x-amz-acl": "public-read",
          },
          body: file,
        });
      }

      const finalS3Url = s3CleanUrl || localUrl;

      dispatch(
        setAddressImage({
          name,
          image: {
            name: file.name,
            url: finalS3Url,
            s3Url: finalS3Url,
            file: file,
          },
        })
      );

      // 2. OCR Address Extraction API call
      if (name === "aadhaarBack") {
        try {
          const formData = new FormData();
          formData.append("aadhar_card", file);
          const ocrRes = await axios.post(denaveAadharFetchApi, formData, { headers });
          const extracted =
            ocrRes.data?.data?.extracted_data || ocrRes.data?.extracted_data || ocrRes.data?.data || ocrRes.data;

          if (extracted) {
            const addrObj = extracted.address_details || {};
            const streetVal = extracted.address || extracted.street || extracted.street_name || extracted.house_number || extracted.loc || "";
            const townVal = extracted.town || extracted.vtc || extracted.loc || "";
            const cityVal = addrObj.City || addrObj.city || extracted.city || extracted.district || extracted.vtc || extracted.loc || "";
            const rawState = addrObj.State || addrObj.state || extracted.state || extracted.state_name || "";
            const stateVal = matchStateId(rawState, activeStateList);
            const pincodeVal = String(addrObj.PIN || addrObj.pin || addrObj.PINCODE || addrObj.pincode || extracted.pincode || extracted.pin || extracted.postal_code || "").replace(/\D/g, "").slice(0, 6);

            if (streetVal) updateTextField("street_name1", streetVal);
            if (townVal) updateTextField("town_name1", townVal);
            if (cityVal) updateTextField("city_name1", cityVal);
            if (stateVal && stateVal !== "Select") {
              updateField("state_name1", stateVal);
              updateField("state_id1", stateVal);
            }
            if (pincodeVal) updatePincode("pin_code1", pincodeVal);

            if (sameAsPermanent) {
              if (streetVal) updateTextField("street_name", streetVal);
              if (townVal) updateTextField("town_name", townVal);
              if (cityVal) updateTextField("city_name", cityVal);
              if (stateVal && stateVal !== "Select") {
                updateField("state_name", stateVal);
                updateField("state_id", stateVal);
              }
              if (pincodeVal) updatePincode("pin_code", pincodeVal);
            }
          }
        } catch (ocrErr) {
          console.error("Aadhaar Back OCR detail fetch error:", ocrErr);
        }
      }
    } catch (err) {
      console.error("Presigned URL upload error:", err);
    } finally {
      dispatch(getLoaderAction2(false));
    }
  };

  const removeImage = () => {
    if (aadhaarBackImage?.url) {
      URL.revokeObjectURL(aadhaarBackImage.url);
    }

    dispatch(removeAddressImage("aadhaarBack"));

    // Clear filled address data fields
    updateField("street_name1", "");
    updateField("town_name1", "");
    updateField("city_name1", "");
    updateField("state_name1", "Select");
    updateField("pin_code1", "");
    updateField("state_id1", "");

    updateField("street_name", "");
    updateField("town_name", "");
    updateField("city_name", "");
    updateField("state_name", "Select");
    updateField("pin_code", "");
    updateField("state_id", "");
  };

  const handleSameAsPermanent = (event) => {
    const checked = event.target.checked;

    dispatch(setSameAsPermanent(checked));

    if (checked) {
      updateField("street_name", form.street_name1 || "");
      updateField("town_name", form.town_name1 || "");
      updateField("city_name", form.city_name1 || "");
      updateField("state_name", form.state_name1 || "Select");
      updateField("pin_code", form.pin_code1 || "");
      updateField("state_id", form.state_id1 || "");
    }
  };

  const getMissingFields = useCallback(() => {
    const missingObjects = requiredFields.filter((field) => {
      const value = form[field.name] || "";

      if (field.name === "state_name" || field.name === "state_name1") {
        return !value || value === "Select";
      }

      if (field.name === "pin_code" || field.name === "pin_code1") {
        return value.length !== 6;
      }

      return value.trim().length === 0;
    });

    const missingNames = new Set(missingObjects.map((f) => f.name));

    if (!aadhaarBackImage) {
      missingNames.add("aadhaarBack");
    }

    setErrorFields(missingNames);

    const missingLabels = missingObjects.map((f) => f.label);

    if (!aadhaarBackImage) {
      missingLabels.push("Aadhaar Backside image");
    }

    return missingLabels;
  }, [aadhaarBackImage, form]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields]
  );

  const renderAddressFields = (type, disabled = false) => {
    const isPermanent = type === "permanent";

    const fieldNames = isPermanent
      ? {
        street: "street_name1",
        town: "town_name1",
        city: "city_name1",
        state: "state_name1",
        pincode: "pin_code1",
        stateId: "state_id1",
      }
      : {
        street: "street_name",
        town: "town_name",
        city: "city_name",
        state: "state_name",
        pincode: "pin_code",
        stateId: "state_id",
      };

    return (
      <div className={styles.addressGrid}>
        <label className={`${styles.field} ${styles.streetField}`}>
          <span>
            Street name<b>*</b>
          </span>
          <textarea
            value={form[fieldNames.street] || ""}
            disabled={disabled}
            rows={3}
            className={
              errorFields.has(fieldNames.street) ? styles.inputError : ""
            }
            onChange={(event) =>
              updateTextField(fieldNames.street, event.target.value)
            }
          />
          {errorFields.has(fieldNames.street) && (
            <p className={styles.fieldError}>Please fill this field</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            Town
          </span>
          <input
            value={form[fieldNames.town] || ""}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.town) ? styles.inputError : ""
            }
            onChange={(event) =>
              updateTextField(fieldNames.town, event.target.value)
            }
          />
          {errorFields.has(fieldNames.town) && (
            <p className={styles.fieldError}>Please fill this field</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            City<b>*</b>
          </span>
          <input
            value={form[fieldNames.city] || ""}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.city) ? styles.inputError : ""
            }
            onChange={(event) =>
              updateTextField(fieldNames.city, event.target.value)
            }
          />
          {errorFields.has(fieldNames.city) && (
            <p className={styles.fieldError}>Please fill this field</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            State<b>*</b>
          </span>
          <select
            value={form[fieldNames.stateId] || form[fieldNames.state] || "Select"}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.state) ? styles.inputError : ""
            }
            onChange={(event) => {
              const selectedVal = event.target.value;
              updateField(fieldNames.state, selectedVal);
              updateField(fieldNames.stateId, selectedVal);
            }}
          >
            <option value="Select">Select</option>
            {activeStateList.map((st) => (
              <option key={st.id || st.state_name} value={st.id}>
                {st.state_name || st.name}
              </option>
            ))}
          </select>
          {errorFields.has(fieldNames.state) && (
            <p className={styles.fieldError}>Please select a state</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            Pincode<b>*</b>
          </span>
          <input
            value={form[fieldNames.pincode] || ""}
            disabled={disabled}
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]*"
            className={
              errorFields.has(fieldNames.pincode) ? styles.inputError : ""
            }
            onChange={(event) =>
              updatePincode(fieldNames.pincode, event.target.value)
            }
          />
          {errorFields.has(fieldNames.pincode) && (
            <p className={styles.fieldError}>
              Please enter a valid 6-digit pincode
            </p>
          )}
        </label>
      </div>
    );
  };

  return (
    <div className={styles.basicInformation}>
      <header className={styles.sectionHeader}>Address Details</header>

      <div className={styles.content}>
        <p className={styles.note}>
          Note: Upload the Aadhaar Back picture. Verify that the file is in JPG,
          JPEG, or PNG format and that its size is less than 10 MB.
        </p>

        <div className={styles.uploadGridSingle}>
          <div className={styles.uploadBlock}>
            <label className={styles.label}>
              Aadhaar Backside image<span>*</span>
            </label>

            <div className={styles.previewRow}>
              {aadhaarBackImage ? (
                <>
                  <img
                    className={styles.imagePreview}
                    src={aadhaarBackImage.url}
                    alt="Aadhaar backside preview"
                  />
                  <button
                    className={styles.removeButton}
                    type="button"
                    onClick={removeImage}
                  >
                    Remove Image
                  </button>
                </>
              ) : (
                <label className={styles.uploadButton}>
                  Upload Image
                  <input
                    className={styles.hiddenInput}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(event) =>
                      handleFileInputChange("aadhaarBack", event)
                    }
                  />
                </label>
              )}
            </div>

            {errorFields.has("aadhaarBack") && (
              <p className={styles.fieldError}>Please upload this image</p>
            )}
          </div>
        </div>

        <h2 className={styles.subsectionTitle}>Permanent Address</h2>
        {renderAddressFields("permanent")}

        <label className={styles.sameAddress}>
          <input
            type="checkbox"
            checked={sameAsPermanent}
            onChange={handleSameAsPermanent}
          />
          <span>Same as Permanent Address</span>
        </label>

        <h2 className={styles.subsectionTitle}>Present Address</h2>
        {renderAddressFields("present", sameAsPermanent)}
      </div>

      {cropperOpen && selectedImageEvent && (
        <ImageCropper
          updateAvatar={() => { }}
          selectedImage={selectedImageEvent}
          getCroppedImg={getCroppedImg}
          closeModal={() => setCropperOpen(false)}
        />
      )}
    </div>
  );
});

export default AddressDetails;
